import { useMemo, useState } from 'react';
import './adc.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useReasonGate } from '../../../components/confirm';
import { runWithAuditReason } from '../../../api/client';
import { useAuthStore } from '../../../auth/store';
import { UserPickerDialog } from './UserPickerDialog';
import { useAdcRows, useCopyAdcRole, ADC_ROLE_LABEL, errMessage, type AdcRow, type AdcRole } from './adc';

interface Props { onHome?: () => void; }

interface PickerTarget { testMethodId: string; role: AdcRole; rowLabel: string; }

/**
 * Settings ▸ Analysis Department Chain — mirrors the legacy XAF
 * `TestMethod_ListView_AnalysisDepartmentChain`: a grid of TestMethods
 * (Matrix / Test / Method, read-only) where the ResultEntryUsers /
 * SDMSValidation / ResultApproval cells show "(N) Users" links; clicking one
 * opens the Available↔Selected user picker for that role.
 */
export function AnalysisDepartmentChainPage({ onHome }: Props) {
  const { data: rows = [], isFetching, refetch } = useAdcRows();
  const loadMs = useFetchMs(isFetching);
  const notify = useNotify();
  const { alert } = useConfirm();
  const gateReason = useReasonGate();
  // Write access = admin (Intelligence has no per-nav write level). Non-admins
  // see the grid read-only — no edit picker, no copy.
  const isAdmin = useAuthStore((s) => s.permissions?.isAdmin ?? false);
  const copyRole = useCopyAdcRole();
  const [search, setSearch] = useState('');
  const [picker, setPicker] = useState<PickerTarget | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // Right-click "Copy To All Cell" context menu (source row + role at cursor).
  const [ctx, setCtx] = useState<{ x: number; y: number; sourceId: string; role: AdcRole } | null>(null);

  const doCopy = async () => {
    if (!ctx) return;
    const { sourceId, role } = ctx;
    const targets = [...selectedIds].map(String).filter((id) => id !== sourceId);
    setCtx(null);
    if (targets.length === 0) { notify.error('Select target rows (other than the source).'); return; }
    // Reason-for-change gate (standing rule 11) — copying a role assignment is an
    // Update; prompts only when ReasonRequired is ON.  null = cancelled → abort.
    const reason = await gateReason('Analysis Department Chain', 'edit');
    if (reason === null) return;
    try {
      const res = await runWithAuditReason(reason, () =>
        copyRole.mutateAsync({ sourceTestMethodId: sourceId, role, targetTestMethodIds: targets }));
      const updated = (res as { data?: { data?: { updated?: number } } })?.data?.data?.updated ?? targets.length;
      await alert({ title: 'Success', message: `${ADC_ROLE_LABEL[role]} copied to ${updated} row${updated === 1 ? '' : 's'}.`, tone: 'success' });
    } catch (e) { notify.error(errMessage(e)); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.matrix, r.test, r.method].some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  // Legacy sorts Matrix → Test → Method as plain (lexical) strings, blanks first.
  const textCmp = (get: (r: AdcRow) => string | null) => (a: AdcRow, b: AdcRow) => {
    const av = (get(a) ?? '').trim(), bv = (get(b) ?? '').trim();
    if (av === '' && bv === '') return 0;
    if (av === '') return -1;
    if (bv === '') return 1;
    return av.localeCompare(bv, undefined, { sensitivity: 'base' });
  };

  const rowLabel = (r: AdcRow) =>
    [r.matrix, r.test, r.method].filter(Boolean).join(' / ') || '(unnamed)';

  // Legacy parity: a 0-count cell renders BLANK (not "(0) Users"), but stays
  // clickable to assign users (a faint "+ Assign" shows on hover).
  // NOTE: `adc-cellbadge` (contains "badge") exempts the link from the global
  // blackText.css rule that forces all grid-cell text to #000 !important — that
  // rule excludes pill/chip/badge/status classes, so this keeps the red.
  const roleCell = (r: AdcRow, role: AdcRole, count: number) => {
    const cls = `adc-link adc-cellbadge${count > 0 ? '' : ' adc-link-empty'}`;
    const label = count > 0 ? `(${count}) Users` : '';
    // Non-admins: read-only (keeps the red, no edit/copy interactions).
    if (!isAdmin) return <span className={cls} style={{ pointerEvents: 'none' }}>{label}</span>;
    return (
      <button
        className={cls}
        title="Double-click to assign · right-click to copy to selected rows"
        onDoubleClick={(e) => { e.stopPropagation(); setPicker({ testMethodId: r.id, role, rowLabel: rowLabel(r) }); }}
        onContextMenu={(e) => {
          if (selectedIds.size === 0) return;          // need target rows checked
          e.preventDefault();
          setCtx({ x: e.clientX, y: e.clientY, sourceId: r.id, role });
        }}
      >
        {label}
      </button>
    );
  };

  const columns: AppColumn<AdcRow>[] = [
    // Matrix keeps the default sort (LIMS sorts by Matrix). Test / Method stay
    // filterable but are NOT click-sortable (no ⇅ icon), like the legacy grid.
    // Balanced widths (roughly equal, filling the row) to match the legacy grid.
    // The checkbox column is pinned tight in adc.css so it never stretches; the
    // slack distributes across these data columns proportionally.
    { field: 'matrix', header: 'Matrix', width: 200, minWidth: 110, text: (r) => r.matrix ?? '', compare: textCmp((r) => r.matrix) },
    { field: 'test',   header: 'Test',   width: 280, minWidth: 180, sortable: false, text: (r) => r.test ?? '' },
    { field: 'method', header: 'Method', width: 240, minWidth: 150, sortable: false, text: (r) => r.method ?? '' },
    // "(N) Users" columns: LEFT-aligned red links; FILTER kept (LIMS has it),
    // sort removed. `text` drives the filter list ("(N) Users" / blank).
    { field: 'resultEntryCount',    header: 'ResultEntryUsers', width: 220, minWidth: 140, align: 'left', sortable: false, text: (r) => r.resultEntryCount > 0 ? `(${r.resultEntryCount}) Users` : '', render: (r) => roleCell(r, 'resultEntry', r.resultEntryCount) },
    { field: 'sdmsValidationCount', header: 'SDMSValidation',   width: 220, minWidth: 140, align: 'left', sortable: false, text: (r) => r.sdmsValidationCount > 0 ? `(${r.sdmsValidationCount}) Users` : '', render: (r) => roleCell(r, 'sdmsValidation', r.sdmsValidationCount) },
    { field: 'resultApprovalCount', header: 'ResultApproval',   width: 220, minWidth: 140, align: 'left', sortable: false, text: (r) => r.resultApprovalCount > 0 ? `(${r.resultApprovalCount}) Users` : '', render: (r) => roleCell(r, 'resultApproval', r.resultApprovalCount) },
  ];

  return (
    <div className="adc-page">
      <div className="adc-top">
        <div className="adc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Analysis Department Chain</span>
        </div>
        {/* BO UI Standard v2: FormTitle (title + single load-time chip + audit chip)
            ON the grey page, then the "N items" subtitle — never inside the card. */}
        <FormTitle pageId="adc-chain" loadMs={loadMs ?? null} loading={isFetching}
          auditFormName="Analysis Department Chain" />
        <div className="adc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>

      {/* Toolbar on its OWN full-width row — shared <AppToolbar> (§18.6):
          Refresh left, Export pushed hard right via the shared spacer. */}
      <div style={{ padding: '0 20px' }}>
        <AppToolbar
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="AnalysisDepartmentChain"
                headers={['Matrix', 'Test', 'Method', 'ResultEntryUsers', 'SDMSValidation', 'ResultApproval']}
                rows={visible.map((r) => {
                  // Blank for 0 users (matches the grid cells), "(N) Users" otherwise.
                  const u = (n: number) => (n > 0 ? `(${n}) Users` : '');
                  return [
                    r.matrix ?? '', r.test ?? '', r.method ?? '',
                    u(r.resultEntryCount), u(r.sdmsValidationCount), u(r.resultApprovalCount),
                  ];
                })} />
            </span>
          }
        />
        {/* Search — shared .list-search-row on the grey page (BO UI Standard),
            below the toolbar, never inside the card. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
      </div>

      {/* ONE white grid card — grid + its single footer only. */}
      <div className="adc-card">
        <AppDataGrid<AdcRow>
          rows={visible}
          columns={columns}
          getRowId={(r) => r.id}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          initialSort={{ key: 'matrix', dir: 'asc' }}
          loading={isFetching}
          emptyText="No tests found."
        />
      </div>

      {picker && (
        <UserPickerDialog
          testMethodId={picker.testMethodId}
          role={picker.role}
          rowLabel={picker.rowLabel}
          onClose={() => setPicker(null)}
          onSaved={() => { /* list auto-invalidates via the mutation */ }}
        />
      )}

      {/* "Copy To All Cell" context menu (right-click a role cell with rows checked). */}
      {ctx && (
        <>
          <div className="adc-ctx-backdrop" onMouseDown={() => setCtx(null)} onContextMenu={(e) => { e.preventDefault(); setCtx(null); }} />
          <div className="adc-ctx" style={{ left: ctx.x, top: ctx.y }}>
            <button className="adc-ctx-item" onClick={() => void doCopy()}>
              <i className="ti ti-copy" /> Copy {ADC_ROLE_LABEL[ctx.role]} to {[...selectedIds].filter((id) => String(id) !== ctx.sourceId).length} selected row{[...selectedIds].filter((id) => String(id) !== ctx.sourceId).length === 1 ? '' : 's'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
